import { useId, useRef, useState } from "react";
import { Button } from "./Button";
import { DialogError } from "./Dialog";

export function DialogUpload({ value, disabled, accept, maxBytes, hint, onChoose, onRemove, onBusyChange }: {
  value?: string | null;
  disabled: boolean;
  accept: string;
  maxBytes: number;
  hint: string;
  onChoose(file: File): Promise<boolean>;
  onRemove(): void;
  onBusyChange(busy: boolean): void;
}) {
  const id = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState("");
  const [over, setOver] = useState(false);
  const filled = Boolean(value);
  const human = (bytes: number) => bytes < 1024 ? `${bytes} bytes` : bytes < 1048576 ? `${Math.round(bytes / 1024)} KB` : `${(bytes / 1048576).toFixed(1)} MB`;

  async function take(selected?: File) {
    if (!selected || disabled) return;
    if (!accept.split(",").includes(selected.type)) { setError("Choose a PNG logo."); return; }
    if (selected.size > maxBytes) { setError(`That file is ${human(selected.size)}. The limit is ${human(maxBytes)}.`); return; }
    setError("");
    onBusyChange(true);
    try { if (await onChoose(selected)) setFile(selected); else setError("The logo could not be read."); }
    finally { onBusyChange(false); }
  }

  return <div className={`fld${error ? " is-invalid" : ""}`}>
    <span className="fld-label" id={`${id}-label`}>Logo <span className="opt">(optional)</span></span>
    <div className={`upload${over ? " is-over" : ""}`} data-state={filled ? "filled" : "empty"} data-max-bytes={maxBytes}
      onDragEnter={event => { event.preventDefault(); if (!disabled) setOver(true); }}
      onDragOver={event => { event.preventDefault(); if (!disabled) setOver(true); }}
      onDragLeave={event => { event.preventDefault(); setOver(false); }}
      onDrop={event => { event.preventDefault(); setOver(false); void take(event.dataTransfer.files[0]); }}>
      <input ref={inputRef} type="file" name="logo" accept={accept} disabled={disabled} tabIndex={filled ? -1 : 0}
        aria-labelledby={`${id}-label`} aria-describedby={`${id}-hint ${id}-error`} aria-invalid={error ? true : undefined}
        onChange={event => void take(event.currentTarget.files?.[0])}/>
      <span className="upload-thumb" aria-hidden="true">{filled ? <img alt="" src={value!}/> : <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"><rect x="2.5" y="3" width="13" height="12" rx="2"/><circle cx="6.5" cy="7" r="1.3"/><path d="M3 13l3.5-3.5 2.5 2.5 2-2 4 4"/></svg>}</span>
      <span className="upload-text upload-empty" hidden={filled}>
        <span><span className="upload-cta">Upload a logo</span> or drop it here</span>
        <span className="upload-meta" id={`${id}-hint`}>{hint}</span>
      </span>
      <span className="upload-text upload-filled" hidden={!filled}><span className="upload-name">{file?.name ?? "Current logo"}</span><span className="upload-meta">{file ? human(file.size) : "Current logo"}</span></span>
      <span className="upload-acts upload-filled" hidden={!filled}>
        <Button appearance="dialog" variant="quiet" data-upload-replace disabled={disabled} onClick={() => inputRef.current?.click()}>Replace</Button>
        <Button appearance="dialog" variant="quiet" data-upload-remove disabled={disabled} onClick={() => {
          setFile(null); setError(""); onRemove();
          if (inputRef.current) { inputRef.current.value = ""; requestAnimationFrame(() => inputRef.current?.focus()); }
        }}>Remove</Button>
      </span>
    </div>
    <DialogError id={`${id}-error`}>{error}</DialogError>
  </div>;
}
